import React, { useEffect, useRef, useState, useCallback } from 'react';
import { API_URL, apiFetch } from '../api';

/**
 * APS (Autodesk Platform Services) Viewer component.
 *
 * Loads the Autodesk Viewer SDK, triggers backend translation,
 * polls until ready, then renders the 2D/3D model inline.
 *
 * Props:
 *   projectId  – KanPlan project ID
 *   documentId – KanPlan document ID
 *   filename   – display name (for error messages)
 *   onError    – (msg: string) => void
 */

const VIEWER_CDN = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
const VIEWER_CSS = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css';

let _sdkLoadPromise = null;

function loadViewerSdk() {
  if (_sdkLoadPromise) return _sdkLoadPromise;
  if (window.Autodesk?.Viewing) return Promise.resolve();

  _sdkLoadPromise = new Promise((resolve, reject) => {
    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = VIEWER_CSS;
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = VIEWER_CDN;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      _sdkLoadPromise = null;
      reject(new Error('Kunde inte ladda Autodesk Viewer SDK'));
    };
    document.head.appendChild(script);
  });
  return _sdkLoadPromise;
}

const STATUS_LABELS = {
  pending: 'Skickar till Autodesk...',
  inprogress: 'Konverterar fil...',
  success: 'Klart',
  failed: 'Konvertering misslyckades',
};

export default function ApsViewerPanel({ projectId, documentId, filename, onError }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  const [phase, setPhase] = useState('init'); // init | translating | loading-sdk | viewing | error
  const [statusText, setStatusText] = useState('Startar...');
  const [progress, setProgress] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fail = useCallback((msg) => {
    setPhase('error');
    setErrorMsg(msg);
    onError?.(msg);
  }, [onError]);

  // Cleanup viewer on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
      if (viewerRef.current) {
        try { viewerRef.current.finish(); } catch { /* ignore */ }
        viewerRef.current = null;
      }
    };
  }, []);

  // Main flow: translate → poll → init viewer
  useEffect(() => {
    if (!projectId || !documentId) return;

    let cancelled = false;

    async function run() {
      // Step 1: Start translation
      setPhase('translating');
      setStatusText(STATUS_LABELS.pending);

      let urn;
      try {
        const res = await apiFetch(
          `${API_URL}/projects/${projectId}/documents/${documentId}/aps/translate`,
          { method: 'POST' }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        urn = data.urn;

        if (data.status === 'success') {
          // Already translated — skip polling
          await initViewer(urn);
          return;
        }
      } catch (e) {
        if (!cancelled) fail(`Översättning misslyckades: ${e.message}`);
        return;
      }

      if (cancelled || !urn) return;

      // Step 2: Poll status
      const poll = async () => {
        try {
          const res = await apiFetch(
            `${API_URL}/projects/${projectId}/documents/${documentId}/aps/status`
          );
          if (!res.ok) return;
          const data = await res.json();

          if (cancelled || !mountedRef.current) return;

          setStatusText(STATUS_LABELS[data.status] || data.status);
          setProgress(data.progress || '');

          if (data.status === 'success') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            await initViewer(data.urn || urn);
          } else if (data.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            const errDetail = (data.messages || []).join('; ') || 'Okänt fel';
            fail(`Konvertering misslyckades: ${errDetail}`);
          }
        } catch { /* network glitch, retry on next interval */ }
      };

      pollRef.current = setInterval(poll, 3000);
      // Immediate first poll
      poll();
    }

    async function initViewer(urn) {
      if (cancelled || !mountedRef.current) return;
      setPhase('loading-sdk');
      setStatusText('Laddar Viewer...');

      try {
        await loadViewerSdk();
      } catch (e) {
        if (!cancelled) fail(e.message);
        return;
      }

      if (cancelled || !mountedRef.current || !containerRef.current) return;

      // Get viewer token
      let accessToken, expiresIn;
      try {
        const res = await apiFetch(`${API_URL}/aps/viewer-token`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        accessToken = data.access_token;
        expiresIn = data.expires_in;
      } catch (e) {
        if (!cancelled) fail(`Kunde inte hämta viewer-token: ${e.message}`);
        return;
      }

      if (cancelled || !mountedRef.current || !containerRef.current) return;

      // Initialize Autodesk Viewer
      setPhase('viewing');
      setStatusText('');

      const Autodesk = window.Autodesk;
      const options = {
        env: 'AutodeskProduction2',
        api: 'streamingV2',
        getAccessToken: (onGetAccessToken) => {
          onGetAccessToken(accessToken, expiresIn);
        },
      };

      Autodesk.Viewing.Initializer(options, () => {
        if (cancelled || !mountedRef.current || !containerRef.current) return;

        const viewer = new Autodesk.Viewing.GuiViewer3D(containerRef.current);
        viewer.start();
        viewerRef.current = viewer;

        const documentId = `urn:${urn}`;
        Autodesk.Viewing.Document.load(
          documentId,
          (doc) => {
            if (cancelled || !mountedRef.current) return;
            const viewable = doc.getRoot().getDefaultGeometry();
            if (viewable) {
              viewer.loadDocumentNode(doc, viewable);
            } else {
              fail('Ingen visningsbar geometri hittades i filen.');
            }
          },
          (code, message) => {
            if (!cancelled) fail(`Viewer-fel (${code}): ${message || 'Okänt'}`);
          }
        );
      });
    }

    run();

    return () => {
      cancelled = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [projectId, documentId, fail]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#1a1a2e' }}>
      {phase !== 'viewing' && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          zIndex: 10,
          color: phase === 'error' ? '#f87171' : '#cbd5e1',
          fontSize: 14,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}>
          {phase === 'error' ? (
            <>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
              <div style={{ maxWidth: 420, textAlign: 'center', lineHeight: 1.6, fontSize: 13.5 }}>{errorMsg}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {filename || 'Okänd fil'}
              </div>
            </>
          ) : (
            <>
              {/* Animated spinner */}
              <div style={{ position: 'relative', width: 48, height: 48, marginBottom: 4 }}>
                <svg width="48" height="48" viewBox="0 0 48 48" style={{ animation: 'aps-spin 1.2s linear infinite' }}>
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#334155" strokeWidth="3" />
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#3b82f6" strokeWidth="3"
                    strokeDasharray="80 50" strokeLinecap="round" />
                </svg>
              </div>

              {/* Phase label */}
              <div style={{ fontWeight: 600, fontSize: 14.5, color: '#e2e8f0' }}>{statusText}</div>

              {/* Progress bar (when translating) */}
              {progress && progress !== '0%' && progress !== 'complete' && (
                <div style={{ width: 180, height: 4, borderRadius: 2, background: '#1e293b', overflow: 'hidden', marginTop: -2 }}>
                  <div style={{
                    height: '100%',
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                    width: progress,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              )}

              {/* Descriptive subtext per phase */}
              <div style={{ fontSize: 12.5, color: '#64748b', textAlign: 'center', maxWidth: 320, lineHeight: 1.5, marginTop: 2 }}>
                {phase === 'init' && 'Förbereder visning...'}
                {phase === 'translating' && (
                  <>Filen konverteras av Autodesk. Detta tar vanligtvis 10–30 sek för en DWG.</>
                )}
                {phase === 'loading-sdk' && 'Viewer-komponenten laddas...'}
              </div>

              {/* Filename chip */}
              <div style={{
                marginTop: 6,
                padding: '4px 12px',
                borderRadius: 6,
                background: '#1e293b',
                fontSize: 11.5,
                color: '#94a3b8',
                fontFamily: 'monospace',
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {filename || 'dokument'}
              </div>
            </>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          opacity: phase === 'viewing' ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      />
      <style>{`
        @keyframes aps-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
